import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import i18n from './i18n';
import AlertMessage from "./components/alert";
import { useClarity } from "./hooks/useClarity";

const TransactionStatus = React.lazy(() => import("./components/index"));

function App({ ux, axiosInstance, token }) {
  const [loader, setLoader] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ message: "", type: "", icon: "" });
  const [tenant, setTenant] = useState("default");
  const [tenantReady, setTenantReady] = useState(false);
  const clarityId = process.env.REACT_APP_CLARITY_ID;
  useClarity(clarityId);

  const setAlertMessageFn = useCallback((e) => {
    setAlertMessage(e);
    const timer = setTimeout(() => setAlertMessage({ message: "", type: "" }), 8000);
    return () => clearTimeout(timer); 
  }, []);

  const detectTenant = () => {
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];
    const knownTenants = (process.env.REACT_APP_KNOWN_TENANTS || '').split(',');
    const normalizedSubdomain = subdomain.replace(/[-_.]/g, '').toLowerCase();
    const matchedTenant = knownTenants.find(tenant =>
      normalizedSubdomain.includes(tenant.toLowerCase())
    );

    return matchedTenant || 'default';
  };

  const validateTenantAssets = async (tenant) => {
    try {
      const response = await fetch(`./assets/${tenant}/style.css`, { method: 'HEAD' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return true;
    } catch (error) {
      console.warn(`Error validando estilo para el tenant "${tenant}":`, error);
      return false;
    }
  };

  const loadTenantStyles = (tenant) => {
    const linkId = "tenant-style";
    const existingLink = document.getElementById(linkId);
    const href = `./assets/${tenant}/style.css`;
    if (existingLink) {
      existingLink.href = href;
    } else {
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    }
  };

  useEffect(() => {
    const init = async () => {
      const detectedTenant = detectTenant();
      setTenant(detectedTenant);

      const tenantAssetsValid = await validateTenantAssets(detectedTenant);
      if (tenantAssetsValid) {
        loadTenantStyles(detectedTenant);
        setTenantReady(true);
      } else {
        setTenantReady(false);
        setAlertMessage({
          message: `No se pudo cargar el tenant "${detectedTenant}"`,
          type: "notif notif-error visible",
          icon: "fa fa-exclamation-triangle"
        });
      }
    };

    init();
  }, []);

  return (
    <div className="App">
      {loader && (
        <div className={ux.loadingPpal}>
          <div className="d-flex justify-content-center">
            <div className="spinner-border spiner">
              <output className="visually-hidden" />
            </div>
          </div>
        </div>
      )}
      {tenantReady && i18n.isInitialized && (
        <I18nextProvider i18n={i18n}>
          <Router>
            <Routes>
              <Route
                exact
                path="/"
                element={
                  <TransactionStatus
                    ux={ux}
                    axiosInstance={axiosInstance}
                    setLoader={setLoader}
                    setAlertMessage={setAlertMessageFn}
                    token={token}
                    resolvedTenant={tenant}
                    i18n={i18n}
                  />
                }
              />
            </Routes>
          </Router>
        </I18nextProvider>
      )}
      <AlertMessage alertMessage={alertMessage} />
    </div>
  );
}

export default App;
